import Button from "@components/Button";
import ScalableWrapper from "@components/ScalableWrapper";
import View from "@components/View";
import { UIContextType } from "@playroomkit/pr-wars-data";
import {
  AudioCueEvent,
  DisableUIOverlayEvent,
  PlayPreloadAudioEvent,
  UIContextEvent,
  WarsEvents,
} from "@playroomkit/pr-wars/events";
import { useShop } from "@src/contexts/ShopProvider";
import { useUrlMapper } from "@src/contexts/URLMapperContext";
import { images } from "@utils/media";
import { useEffect, useRef, useState } from "react";
import { useBots } from "@src/contexts/BotsProvider";

const BotSelectionUI = () => {
  const urlMapper = useUrlMapper();
  const { showPrice, handlePurchaseClick } = useShop();
  const { bots, toggleBot, updateBots } = useBots();

  const [showUI, setShowUI] = useState(false);
  const slideRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleUIContextEvent = async (event: Event) => {
      let e = event as UIContextEvent,
        display = e.hasContext(UIContextType.ADDBOT);

      if (display) {
        setShowUI((prevState) => !prevState);

        document.dispatchEvent(new PlayPreloadAudioEvent('sfx', 'point', 1, false));
        // Send analytic event
        const analytics = await import('../../analytics/analytics.js');

        await analytics.track('load-scene', {
          event: 'load-scene',
          logType: 'load-scene',
          sceneId: 'bot-selection',
          t: Date.now(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          localTime: new Date().toLocaleString(),
      });
      }
    };

    document.addEventListener(WarsEvents.UIContextEvent, handleUIContextEvent);

    // Cleanup function to remove event listeners
    return () => {
      document.removeEventListener(
        WarsEvents.UIContextEvent,
        handleUIContextEvent
      );
    };
  }, []);

  useEffect(() => {
    if (showUI) {
      updateBots();
    }
  }, [showUI]);

  const hideUI = () => {
    setShowUI(false);
    document.dispatchEvent(new DisableUIOverlayEvent());
    playButtonSfx();
  };

  const onBotToggle = (id: string) => {
    toggleBot(id);
    playButtonSfx();
  };

  const next = () => {
    const lastBotEl = document.getElementById(`bot-id-${bots.length - 1}`);
    if (
      slideRef.current &&
      lastBotEl?.getBoundingClientRect().right! > window.innerWidth - 20
    ) {
      const slideStyle = slideRef.current.style.transform;
      const translateX = slideStyle.match(/translateX\((-?\d+)px\)/);
      let currentX = translateX ? parseInt(translateX[1], 10) : 0;
      slideRef.current.style.transform = `translateX(${currentX - 250}px)`;
      playButtonSfx();
    }
  };

  const prev = () => {
    const lastBotEl = document.getElementById(`bot-id-0`);
    if (slideRef.current && lastBotEl?.getBoundingClientRect().left! < 40) {
      const slideStyle = slideRef.current.style.transform;
      const translateX = slideStyle.match(/translateX\((-?\d+)px\)/);
      let currentX = translateX ? parseInt(translateX[1], 10) : 0;
      slideRef.current.style.transform = `translateX(${currentX + 250}px)`;
      playButtonSfx();
    }
  };

  const playButtonSfx = () => {
    document.dispatchEvent(
      new AudioCueEvent("sfx", "point", "audio/sfx/point.mp3", 1, false)
    );
  };

  return (
    <View
      className={`w-screen h-dvh absolute top-0 left-0 bg-cover bg-center bg-no-repeat overflow-x-hidden overflow-y-hidden ${
        showUI ? "opacity-1 z-50" : "opacity-0 -z-50"
      }`}
      style={{ backgroundImage: `url(${images.BotSelectionBackground})` }}
    >
      <ScalableWrapper maintainAspectRatioBy="height" anchor="top-left">
        <View className="w-full absolute top-0 flex items-center justify-start gap-6 pl-10 pt-8">
          <Button className="bg-transparent border-none" onClick={hideUI}>
            <img
              src={images.GoBackIcon}
              alt="go-back-icon"
              className="w-12 h-12 object-contain"
            />
          </Button>
        </View>
      </ScalableWrapper>
      <ScalableWrapper maintainAspectRatioBy="height">
        <View className="w-full absolute top-0 flex items-center justify-center gap-6 pt-4">
          <img
            src={images.AIBotIcon}
            alt="ai-bot-icon"
            className="w-12 h-12 object-contain"
          />
          <h1 className="text-white font-[Slackey]">AI Clones</h1>
        </View>
      </ScalableWrapper>
      <ScalableWrapper maintainAspectRatioBy="height" anchor="left">
        <View
          ref={slideRef}
          className="w-full h-full flex items-center justify-start gap-2 translate-x-28 mt-4 transition-transform ease-in-out duration-300"
        >
          {bots.map((bot, index) => {
            return (
              <View
                key={bot.id}
                id={`bot-id-${index}`}
                className="relative w-96 h-[32rem] flex flex-col items-center gap-6"
              >
                <View className="flex flex-col items-center">
                  <h1
                    className="w-56 h-14 text-center font-[Slackey] text-[#FFF4D0] leading-none"
                    style={{
                      textShadow: "0px 3.36px 0px rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    {bot.name}
                  </h1>
                  <p className="text-center font-mono font-bold text-white leading-tight text-xs text-shadow-md">
                    {bot.about}
                  </p>
                </View>
                <img
                  src={urlMapper.mapUrl(bot.modelImageUrl)}
                  alt="bot-image"
                  className="w-72 h-72 object-contain"
                />
                <View
                  className="flex flex-col items-center absolute -bottom-12"
                  enableEventCapturing
                >
                  {bot.selected && (
                    <img
                      src={images.WhiteTickIcon}
                      alt="tick"
                      className="w-12 h-12 object-contain"
                    />
                  )}
                  {bot.enabled ? (
                    <Button
                      className="bg-transparent border-none"
                      onClick={() => onBotToggle(bot.id)}
                      disabled={!showUI}
                    >
                      <img
                        src={
                          bot.selected
                            ? images.RemoveBotIcon
                            : images.AddBotIcon
                        }
                        alt="add-bot"
                        className="w-56 h-20 object-contain"
                      />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        if (bot.price) {
                          handlePurchaseClick(bot.skuId ?? "");
                        }
                      }}
                      className="font-[Slackey] border-4 border-solid border-[#FFB218] bg-[#01088F] rounded-[12px] w-[237px] h-[52px] flex items-center justify-center text-white text-[21px] mb-[18px]"
                    >
                      {bot.price
                        ? `Buy ${showPrice(bot.price ?? "")}`
                        : "Locked"}
                    </Button>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      </ScalableWrapper>
      <ScalableWrapper maintainAspectRatioBy="height" anchor="left">
        <View className="w-full h-full absolute top-0 left-0 flex items-center justify-start pl-8">
          <Button className="bg-transparent border-none" onClick={prev}>
            <img
              src={images.LeftArrowIcon}
              alt="go-back-icon"
              className="w-16 h-16 object-contain"
            />
          </Button>
        </View>
      </ScalableWrapper>
      <ScalableWrapper maintainAspectRatioBy="height" anchor="right">
        <View className="w-full h-full absolute top-0 right-0 flex items-center justify-end pr-4">
          <Button className="bg-transparent border-none" onClick={next}>
            <img
              src={images.RightArrowIcon}
              alt="go-back-icon"
              className="w-16 h-16 object-contain"
            />
          </Button>
        </View>
      </ScalableWrapper>
    </View>
  );
};

export default BotSelectionUI;
